/**
 * Pure narrative-composition helpers for `NotableGameEvent` records.
 *
 * Each composer takes plain string / number / array inputs and returns a
 * single-sentence English narrative. Composers have no `G`/`ctx` dependency,
 * import no game framework, and import no registry package — they are pure
 * functions, replay-deterministic by construction, and testable in
 * isolation via golden strings.
 *
 * Format strings are byte-stable: identical inputs produce identical output.
 * No `toLocaleString`, no `Intl.*`, no locale-sensitive formatters. No
 * conditional punctuation, no optional clauses that reorder segments.
 * Any change to a format string is a replay-affecting change and requires
 * re-pinning `PRE_WP080_HASH` + the sentinel `finalStateHash`.
 *
 * No `boardgame.io` import. No `@legendary-arena/registry` import.
 */
// ---------------------------------------------------------------------------
// Effect-keyword → human label
// ---------------------------------------------------------------------------
// why: pure-string mapping from machine keyword to a short human phrase used
// inside the Fight / Ambush narratives. Stable byte-for-byte; replay-locked.
// Adding a new keyword to `VillainEffectKeyword` requires adding a matching
// label here AND re-pinning the replay hashes.
const EFFECT_KEYWORD_LABELS = {
    gainWoundEachPlayer: 'every player gained a wound',
    gainWoundCurrentPlayer: 'the active player gained a wound',
    koHeroCurrentPlayer: 'the active player KO’d a hero',
    heroDeckTopToEscape: 'the top of the hero deck escaped',
    captureBystander: 'a bystander was captured',
    koHeroEachPlayer: 'every player KO’d a hero',
    koHeroEachPlayerMag2: 'every player KO’d two heroes',
    captureHqHeroRightmost: "the rightmost HQ hero was captured",
    captureHqHeroHighestCost: "the highest-cost HQ hero was captured",
    captureHqHeroLowestCost: "the lowest-cost HQ hero was captured",
};
/**
 * Returns the human label for a single effect keyword.
 *
 * Falls back to the raw keyword string on out-of-vocabulary input (defensive
 * — `VillainEffectKeyword` is a closed union, but a future widening must not
 * cause emissions to throw).
 *
 * @param effect - The effect keyword to label.
 * @returns Human-readable phrase for narratives.
 */
function labelForEffect(effect) {
    const label = EFFECT_KEYWORD_LABELS[effect];
    if (typeof label === 'string' && label.length > 0)
        return label;
    return effect;
}
/**
 * Names who gained a Wound and who only would gain one because a Diving Block reveal
 * is pending (D-24673): "Player 0 gained a Wound; Player 1 would gain a Wound — Diving
 * Block can prevent it". Either half is left out when its list is empty.
 *
 * // why: D-24673 — the Wound lands first and the reveal resolves afterwards, so a line
 * written now must not call a preventable Wound gained (match P9oVujYd2B9). The
 * reveal ("prevented the Wound") or decline ("took the Wound") line that follows
 * gives the outcome.
 *
 * @param gainedPlayerIds - Players whose Wound nothing can undo.
 * @param divingBlockPendingPlayerIds - Players with a Diving Block reveal pending.
 * @returns The joined clause, with no leading label and no trailing period.
 */
export function composeWoundOutcomeClause(gainedPlayerIds, divingBlockPendingPlayerIds) {
    const clauses = [];
    if (gainedPlayerIds.length > 0) {
        clauses.push(`${formatPlayerList(gainedPlayerIds)} gained a Wound`);
    }
    if (divingBlockPendingPlayerIds.length > 0) {
        clauses.push(`${formatPlayerList(divingBlockPendingPlayerIds)} would gain a Wound — Diving Block can prevent it`);
    }
    return clauses.join('; ');
}
/**
 * Formats player ids as "Player 0, Player 1".
 *
 * @param playerIds - The player ids, in order.
 * @returns The comma-joined player names.
 */
function formatPlayerList(playerIds) {
    return playerIds.map((playerId) => `Player ${playerId}`).join(', ');
}
/**
 * Composes the clause for a `Fight effect:` / `Ambush effect:` / `Escape effect:`
 * log line from the resolved per-effect results.
 *
 * Pure + byte-stable. Each result renders as its generic keyword label with the
 * resolved target names appended in parentheses when present, or the fixed
 * "the active player must KO a hero" phrase ("two heroes" for a two-KO effect) when the
 * KO is still pending. Clauses
 * join with "; " in dispatch order. The caller supplies the leading
 * `<timing> effect: ` label and the trailing period.
 *
 * @param results - The resolved effect results in dispatch order (>= 1).
 * @returns The joined clause string (no leading label, no trailing period).
 */
export function composeEffectResultLogLine(results) {
    const clauses = [];
    for (const result of results) {
        clauses.push(composeEffectResultClause(result));
    }
    return clauses.join('; ');
}
/**
 * Composes one effect-result clause: the pending phrase, or the keyword label
 * with resolved target names appended when any were affected.
 *
 * @param result - One resolved effect result.
 * @returns The clause for this single effect.
 */
function composeEffectResultClause(result) {
    if (result.pending === true) {
        // why: WP-316 — no hero is KO'd yet for a parked interactive KO; the player
        // picks via resolveKoHeroChoice later, so name no target here. The resolved
        // hero IS named at resolve time — resolveKoHeroChoice pushes a "Player N KO'd
        // <name>" log line (the WP-316 §Scope Out resolve-time-naming follow-up).
        // why: D-24644 amendment — a two-KO effect (Juggernaut's Ambush / Escape) owes two
        // picks, so the line says so; the prompt's "(2 remaining)" already did. Every other
        // pending KO keyword owes one.
        let pendingClause = 'the active player must KO a hero';
        if (result.keyword === 'koHeroEachPlayerMag2') {
            pendingClause = 'the active player must KO two heroes';
        }
        // why: D-24783 — a grey-filtered KO (Destroyer: "KOs a non-grey Hero") names the filter.
        if (result.greyFilter !== undefined) {
            if (result.keyword === 'koHeroEachPlayerMag2') {
                pendingClause = `the active player must KO two ${greyHeroNoun(result.greyFilter, 2)}`;
            }
            else {
                pendingClause = `the active player must KO a ${greyHeroNoun(result.greyFilter, 1)}`;
            }
        }
        // why: D-24683 — with the per-player split, name each other player's outcome
        // ("Player 1 KO’d …" / "Player 1 had no hero to KO") instead of a flat list.
        if (result.playerTargetNames !== undefined) {
            const otherClauses = composePlayerKoClauses(result.playerTargetNames, result.greyFilter);
            if (otherClauses.length > 0) {
                return `${pendingClause}; ${otherClauses.join('; ')}`;
            }
            return pendingClause;
        }
        if (result.targetNames.length > 0) {
            // why: D-24644 — an each-player KO parks only the active player's pick; the
            // other players' auto-KOs already happened and are still named.
            return `${pendingClause}; other players KO’d ${result.targetNames.join(', ')}`;
        }
        return pendingClause;
    }
    // why: D-24673 — a gain-wound effect with a Diving Block reveal pending must not say
    // "gained a wound" for that player; the reveal / decline line that follows gives the
    // outcome. Present only when a reveal is pending, so every other clause is unchanged.
    if (result.woundOutcomes !== undefined) {
        if (result.keyword === 'gainWoundCurrentPlayer') {
            return 'the active player would gain a wound — Diving Block can prevent it';
        }
        const outcomes = result.woundOutcomes;
        return `every player gains a wound: ${composeWoundOutcomeClause(outcomes.gainedPlayerIds, outcomes.divingBlockPendingPlayerIds)}`;
    }
    const label = labelForEffect(result.keyword);
    // why: D-24683 — "every player KO’d two heroes (S.H.I.E.L.D. Agent, S.H.I.E.L.D. Agent)"
    // read as if both players lost two when one had nothing to KO (matches GkZeatCQh5f,
    // O7iMuNBh9E5). With the per-player split, say who KO'd what. Nothing KO'd at all keeps
    // the D-24646 "no player had a hero to KO" wording below.
    if (result.playerTargetNames !== undefined && result.targetNames.length > 0) {
        return composePlayerKoClauses(result.playerTargetNames, result.greyFilter).join('; ');
    }
    if (result.targetNames.length > 0) {
        return `${label} (${result.targetNames.join(', ')})`;
    }
    // why: D-24646 — the KO labels are past tense ("every player KO’d two heroes"), so a KO
    // that found no Hero to take (an empty discard pile, say) must not claim one happened.
    const noTargetLabel = NO_TARGET_KO_LABELS[result.keyword];
    if (noTargetLabel !== undefined) {
        // why: D-24783 — a grey-filtered each-player KO that found nothing says which Heroes
        // were missing ("no player had a non-grey Hero to KO").
        if (result.greyFilter !== undefined && result.keyword !== 'koHeroCurrentPlayer') {
            return `no player had a ${greyHeroNoun(result.greyFilter, 1)} to KO`;
        }
        return noTargetLabel;
    }
    return label;
}
/**
 * Composes one clause per player for an each-player KO (D-24683), skipping the player
 * whose pick is still pending (the caller names that one).
 *
 * @param playerTargetNames - Each player's share, in seat order.
 * @param greyFilter - The KO's grey-Hero filter (D-24783), or undefined for an unfiltered KO.
 * @returns "Player N KO’d A, B" or "Player N had no hero to KO", one per non-pending player.
 */
function composePlayerKoClauses(playerTargetNames, greyFilter) {
    // why: D-24783 — Destroyer's 'non-grey' KO skips grey Heroes, so a player holding only
    // S.H.I.E.L.D. starters had no NON-GREY Hero to KO; "no hero" would be false.
    let noHeroClause = 'had no hero to KO';
    if (greyFilter !== undefined) {
        noHeroClause = `had no ${greyHeroNoun(greyFilter, 1)} to KO`;
    }
    const clauses = [];
    for (const playerShare of playerTargetNames) {
        if (playerShare.pending === true) {
            continue;
        }
        if (playerShare.names.length > 0) {
            clauses.push(`Player ${playerShare.playerId} KO’d ${playerShare.names.join(', ')}`);
        }
        else {
            clauses.push(`Player ${playerShare.playerId} ${noHeroClause}`);
        }
    }
    return clauses;
}
/**
 * Names the Heroes a grey filter admits, for a KO log clause (D-24783).
 *
 * @param greyFilter - `'grey'` or `'non-grey'`.
 * @param count - 1 for the singular, anything else for the plural.
 * @returns "non-grey Hero", "grey Heroes", and so on.
 */
function greyHeroNoun(greyFilter, count) {
    const heroWord = count === 1 ? 'Hero' : 'Heroes';
    return `${greyFilter} ${heroWord}`;
}
// why: D-24646 — the resolved-nothing wording for the three KO-a-Hero keywords, the only
// labels that describe a KO as done. Other keywords (wounds, captures) keep their label.
const NO_TARGET_KO_LABELS = {
    koHeroCurrentPlayer: 'the active player had no hero to KO',
    koHeroEachPlayer: 'no player had a hero to KO',
    koHeroEachPlayerMag2: 'no player had a hero to KO',
};
// ---------------------------------------------------------------------------
// Fight narrative
// ---------------------------------------------------------------------------
/**
 * Composes the single-sentence narrative for a `fightResolved` event.
 *
 * Inputs are pure values; no `G`/`ctx` access. `cardName` is the resolved
 * display name (or the raw `cardId` if `cardDisplayData` had no entry).
 *
 * @param cardName - Human-facing name of the defeated card.
 * @param bystandersRescued - Bystanders rescued into the victory pile (>= 0).
 * @param effectResults - Fight: effect results the executor applied, with target
 *   ext_ids already resolved to display names (WP-319).
 * @param skrullGained - True when the defeated card was a Secret Invasion Skrull
 *   (a Hero acting as a Villain) gained into the defeating player's discard
 *   (WP-518 / D-24331), or a Brainwash the Military Officer gained as a Hero
 *   (WP-823 / D-24756). Defaults to false so every non-Skrull caller and the
 *   existing golden tests stay byte-identical.
 * @returns Single English sentence describing the resolved fight.
 */
export function composeFightNarrative(cardName, bystandersRescued, effectResults, skrullGained = false) {
    const bystanderClause = bystandersRescued > 0
        ? ` and rescued ${String(bystandersRescued)} bystander(s)`
        : '';
    // why: WP-518 / D-24331 — Secret Invasion "If you defeat that Hero, you gain
    // it". The engine already routes a defeated Skrull to the defeating player's
    // discard (WP-514 defeatCityVillainCore), but without this clause the overlay
    // reads `Fought "X"` — identical to an ordinary villain kill — so the gain
    // lands silently and players report it as not happening. Empty for every
    // non-Skrull defeat, so those narratives are byte-identical (the notableEvents
    // narrative is hashed; this clause fires only on Skrull defeats, which no
    // committed fixture performs, so the sentinel / PRE_WP080 hashes are unchanged).
    // Third-person voice ("the active player") matches the effect clauses below —
    // the overlay is a shared, all-audience projection, not owner-scoped.
    const skrullGainClause = skrullGained
        ? " and gained the Hero into the active player's discard pile"
        : '';
    // why: WP-319 / D-24105 — the fightResolved narrative (shown by the
    // center-screen NotableEventOverlay) NAMES the specific effect target(s)
    // via the shared composeEffectResultLogLine, matching the durable-log
    // `Fight effect:` line. Supersedes the WP-316 choice to keep this narrative
    // keyword-granular for byte-identity: the sentinel replay fires no fight
    // event, so `finalStateHash` is unchanged (verified). The `appliedEffects`
    // keyword field on the event is unchanged; only this narrative string is enriched.
    const effectClause = effectResults.length > 0
        ? `; Fight effect: ${composeEffectResultLogLine(effectResults)}`
        : '';
    return `Fought "${cardName}"${bystanderClause}${skrullGainClause}${effectClause}.`;
}
// ---------------------------------------------------------------------------
// Ambush narrative
// ---------------------------------------------------------------------------
/**
 * Composes the clause for an Ambush that plays the top N villain-deck cards
 * (The Leader, D-24351), e.g. "plays the top card of the Villain Deck".
 *
 * @param playCount - Number of top villain-deck cards the Ambush plays (>= 1).
 * @returns The clause (no leading label, no trailing period).
 */
export function composeVillainDeckPlayClause(playCount) {
    if (playCount === 1) {
        return 'plays the top card of the Villain Deck';
    }
    return `plays the top ${playCount} cards of the Villain Deck`;
}
/**
 * Composes the single-sentence narrative for an `ambushResolved` event.
 *
 * The unconditional city-entry bystander attach is NOT an Ambush effect
 * and is never described here — only `appliedEffects` returned by
 * `executeVillainAbilities(...,'onAmbush')` are referenced, plus the
 * villain-deck play the reveal fire site performs itself (D-24351).
 *
 * @param cardName - Human-facing name of the revealed villain.
 * @param effectResults - Ambush: effect results the executor applied, with target
 *   ext_ids already resolved to display names (WP-319).
 * @param villainDeckPlayCount - Top villain-deck cards the Ambush plays (0 = none).
 * @returns Single English sentence describing the resolved ambush.
 */
export function composeAmbushNarrative(cardName, effectResults, villainDeckPlayCount = 0) {
    // why: D-24703 — The Leader's "Ambush: Play the top card of the Villain Deck" is
    // played by the reveal fire site, not the executor (its handler is a deliberate
    // no-op, D-24351), so `effectResults` is empty for it. Without this clause the
    // overlay claimed the card "entered the city with no Ambush effect" while its
    // Ambush was visibly resolving (prod match 0BVSi0_KY0A).
    const clauses = [];
    if (effectResults.length > 0) {
        // why: WP-319 / D-24105 — names the specific effect target(s) in the
        // ambushResolved narrative (overlay + log) via the shared
        // composeEffectResultLogLine; supersedes the WP-316 keyword-granular narrative.
        clauses.push(composeEffectResultLogLine(effectResults));
    }
    if (villainDeckPlayCount > 0) {
        clauses.push(composeVillainDeckPlayClause(villainDeckPlayCount));
    }
    if (clauses.length === 0) {
        return `"${cardName}" entered the city with no Ambush effect.`;
    }
    return `"${cardName}" ambushed: ${clauses.join('; ')}.`;
}
// ---------------------------------------------------------------------------
// Scheme twist narrative
// ---------------------------------------------------------------------------
// why: pure-string mapping from camelCase resolver key to a stable English
// phrase. Locked byte-for-byte (replay-affecting) — adding a resolver key
// requires expanding both the `SchemeTwistResolverKey` union AND this map,
// then re-pinning the replay hashes.
const RESOLVER_KEY_PHRASES = {
    revealOrPunish: 'players were forced to reveal a matching hero or suffer a penalty',
    chainedReveals: 'extra villain-deck cards were revealed',
    woundAll: 'every player gained wounds',
    koFromHq: 'heroes were KO’d from the HQ',
    midtownBankRobbery: 'the Bank villain captured bystanders and another card was revealed',
    killbots: 'a Twist was placed next to the Scheme, raising every Killbot’s attack',
    secretInvasion: 'the highest-cost HQ Hero moved into the Sewers as a Skrull Villain',
    portals: 'a Dark Portal opened, raising the attack of the Mastermind or the Villains beneath it',
    clashMonsterPit: 'a Monster climbed out of the Monster Pit to face each player’s Gladiator',
    brainwashTheMilitary: 'a Twist joined the Traitor Battalion, or every S.H.I.E.L.D. Officer in the City escaped',
    goBackInTime: 'a Hero in the HQ must be Purged from the Timestream',
    enigmaCode: 'a card from the Hero Deck joined the face-down Enigma Code',
    changeTheOutcomeOfWwii: 'the Axis invaded a new country and the City changed size',
};
/**
 * Composes the single-sentence narrative for a `schemeTwistResolved` event.
 *
 * @param cardName - Human-facing name of the scheme-twist card.
 * @param resolverKey - Which of the locked resolvers handled the twist.
 * @returns Single English sentence describing the resolved scheme twist.
 */
export function composeSchemeTwistNarrative(cardName, resolverKey) {
    const phrase = RESOLVER_KEY_PHRASES[resolverKey] ?? resolverKey;
    return `Scheme Twist "${cardName}": ${phrase}.`;
}
// ---------------------------------------------------------------------------
// Mastermind strike narrative
// ---------------------------------------------------------------------------
/**
 * Composes the single-sentence narrative for a `mastermindStrikeResolved` event.
 *
 * @param cardName - Human-facing name of the strike card.
 * @returns Single English sentence describing the resolved mastermind strike.
 */
export function composeMastermindStrikeNarrative(cardName) {
    return `Master Strike: "${cardName}" resolved.`;
}
// ---------------------------------------------------------------------------
// Mastermind defeat narrative
// ---------------------------------------------------------------------------
/**
 * Composes the single-sentence narrative for a `mastermindDefeated` event.
 *
 * Inputs are pure values; no `G`/`ctx` access. `mastermindName` is the
 * resolved display name (or the raw mastermind id when `cardDisplayData`
 * had no entry). The bystander clause mirrors `composeFightNarrative` so
 * both rescue messages read identically.
 *
 * @param mastermindName - Human-facing name of the defeated mastermind.
 * @param bystandersRescued - Bystanders rescued into the victory pile (>= 0).
 * @returns Single English sentence describing the mastermind's defeat.
 */
export function composeMastermindDefeatedNarrative(mastermindName, bystandersRescued) {
    const bystanderClause = bystandersRescued > 0
        ? ` and rescued ${String(bystandersRescued)} bystander(s)`
        : '';
    return `Defeated the Mastermind "${mastermindName}"${bystanderClause}.`;
}
/**
 * Composes the single-sentence narrative for a `healResolved` event (WP-381).
 *
 * Pure: given the same count, returns byte-identical output.
 *
 * @param woundsHealed - Number of Wounds KO'd from hand this heal (>= 1).
 * @returns A single English sentence for the notable-event overlay.
 */
export function composeHealNarrative(woundsHealed) {
    return `Used Healing, KO'ing ${String(woundsHealed)} Wound(s) from hand.`;
}
// ---------------------------------------------------------------------------
// Bystander reveal narrative
// ---------------------------------------------------------------------------
/**
 * Composes the single-sentence narrative for a `bystanderRevealed` event (WP-602).
 *
 * Pure + byte-stable: given the same two names, returns identical output. Both
 * inputs are resolved display names (or the raw ext_id when `cardDisplayData`
 * had no entry) supplied by the fire site, so the composer keeps its no-`G` purity.
 *
 * @param bystanderName - Human-facing name of the revealed Bystander card.
 * @param captorName - Human-facing name of the captor (City villain closest to the Villain Deck, or the Mastermind).
 * @returns A single English sentence for the notable-event overlay.
 */
export function composeBystanderRevealedNarrative(bystanderName, captorName) {
    return `Bystander "${bystanderName}" was revealed and captured by "${captorName}".`;
}
// ---------------------------------------------------------------------------
// Deck-reshuffle narrative
// ---------------------------------------------------------------------------
/**
 * Composes the single-sentence narrative for a `deckReshuffled` event (WP-642).
 *
 * Pure + byte-stable: takes no inputs and returns a constant sentence, so
 * every emission is identical — replay-deterministic by construction. Voiced
 * in the third person (audience-neutral) because the notable-event overlay is
 * a public, all-audience projection: a per-seat "your deck" wording would
 * mislead the other players who also see it.
 *
 * @returns A single English sentence for the notable-event overlay.
 */
export function composeDeckReshuffledNarrative() {
    return 'The hero deck was reshuffled from the discard pile.';
}
// ---------------------------------------------------------------------------
// Strike-blocked narrative
// ---------------------------------------------------------------------------
/**
 * Composes the single-sentence narrative for a `strikeBlocked` event
 * (WP-644 / D-24456; `'ambush'` added by WP-646 / D-24458).
 *
 * Pure + byte-stable: a total function of the `threatKind` returning one of
 * three constant sentences, so every emission is identical — replay-deterministic
 * by construction. Voiced in the third person (audience-neutral) because the
 * notable-event overlay is a public, all-audience projection; the blocking
 * seat travels on the event's `playerId`, not in the copy. An explicit `if`
 * arm per value (no nested ternary) per the code-style rules.
 *
 * @param threatKind - Which threat class the player avoided.
 * @returns A single English sentence for the notable-event overlay.
 */
export function composeStrikeBlockedNarrative(threatKind) {
    if (threatKind === 'masterStrike') {
        return 'The Master Strike was blocked.';
    }
    if (threatKind === 'schemeTwist') {
        return 'The Scheme Twist penalty was blocked.';
    }
    if (threatKind === 'ambush') {
        return 'The Ambush was blocked.';
    }
    if (threatKind === 'fight') {
        return 'The villain\'s attack was blocked.';
    }
    if (threatKind === 'escape') {
        // why: WP-651 — the villain HAS already escaped by the time its Escape
        // reveal-or-wound fires (it is in G.escapedPile and counts toward the escape
        // loss); revealing a Hero dodges only the Wound, never the escape. So this
        // says "penalty was blocked" (like schemeTwist), NOT "The Escape was blocked".
        return 'The Escape penalty was blocked.';
    }
    // why: WP-646 — exhaustiveness guard. The arms above narrow `threatKind`
    // to `never`, so this assignment compiles today; a future `StrikeBlockThreatKind`
    // value fails `tsc` here until it gets its own arm, so the composer can never
    // silently mislabel a new threat kind (a bare fallthrough would return the last
    // sentence for it). Compile-time guard on production source — NOT a runtime
    // throw (this composer is called from a villain/mastermind handler, and
    // handlers never throw); the line is unreachable for any valid value.
    const exhaustiveCheck = threatKind;
    return exhaustiveCheck;
}
// ---------------------------------------------------------------------------
// Transform narrative
// ---------------------------------------------------------------------------
/**
 * Composes the single-sentence narrative for a `transformResolved` event
 * (WP-672 / D-24487).
 *
 * Pure + byte-stable: given the same two names, returns identical output. Both
 * inputs are resolved display names (or the raw ext_id when `cardDisplayData`
 * had no entry) supplied by the fire site, so the composer keeps its no-`G`
 * purity — mirroring `composeBystanderRevealedNarrative`. Voiced in the third
 * person because the notable-event overlay is a public, all-audience projection;
 * the transforming seat travels on the event's `playerId`, not in the copy.
 *
 * @param baseName - Human-facing name of the base card that was played.
 * @param secondFormName - Human-facing name of the second form it became.
 * @returns A single English sentence for the notable-event overlay.
 */
export function composeTransformNarrative(baseName, secondFormName) {
    return `"${baseName}" transformed into "${secondFormName}".`;
}
// ---------------------------------------------------------------------------
// Hero-effect resolved narrative (reveal-for-attack, WP-697)
// ---------------------------------------------------------------------------
/**
 * Composes the single-sentence narrative for a `heroEffectResolved` event's
 * reveal-top-of-Hero-Deck-for-attack family (WP-697 / D-24516).
 *
 * Pure + byte-stable: given the same inputs, returns identical output. The card
 * name is a resolved display name (or the raw ext_id when `cardDisplayData` had
 * no entry) supplied by the fire site, so the composer keeps its no-`G` purity —
 * mirroring `composeTransformNarrative`. Voiced in the third person because the
 * notable-event overlay is a public, all-audience projection; the acting seat
 * travels on the event's `playerId`, not in the copy. Mirrors the
 * `heroEffectRevealHeroDeckAttack` `applied` log line minus the `Player N`
 * prefix, so the overlay and the game log read consistently.
 *
 * @param cardName - Human-facing name of the hero card whose ability resolved.
 * @param revealedCount - Number of Hero-Deck cards revealed (>= 1 for a realized reveal).
 * @param totalAttack - Summed printed attack gained from the revealed cards (>= 0).
 * @returns A single English sentence for the notable-event overlay.
 */
export function composeHeroRevealAttackNarrative(cardName, revealedCount, totalAttack) {
    return `"${cardName}" revealed ${String(revealedCount)} card(s) from the Hero Deck and gained +${String(totalAttack)} attack.`;
}
/**
 * Composes the single-sentence narrative for a `heroEffectResolved` event's
 * auto-resolving deck-top reveal family (WP-726 / D-24547) — the sibling of
 * `composeHeroRevealAttackNarrative` for the `heroEffectReveal` handler
 * (`reveal-cost-attack` / `reveal-odd-draw` / `reveal-min` / `reveal-ko` /
 * `reveal-ko-or-draw` / `reveal-ko-attack`).
 *
 * Pure + byte-stable: given the same inputs, returns identical output. Both
 * card names are resolved display names (or the raw ext_id when
 * `cardDisplayData` had no entry) supplied by the fire site, so the composer
 * keeps its no-`G` purity. `outcomeText` is the fire site's
 * `describeRevealActions` phrase (e.g. `"gained attack"`, `"drew it"`,
 * `"KO'd it"`). Voiced in the third person with NO "Player N" prefix (matching
 * the sibling composer): the acting seat travels on the event's `playerId`,
 * not in the copy.
 *
 * @param sourceCardName - Human-facing name of the hero card whose ability resolved.
 * @param revealedCardName - Human-facing name of the revealed deck-top card.
 * @param cost - The revealed card's printed cost (the reveal predicate input).
 * @param outcomeText - The comma-joined outcome phrase from `describeRevealActions`.
 * @returns A single English sentence for the notable-event overlay.
 */
export function composeHeroRevealTopNarrative(sourceCardName, revealedCardName, cost, outcomeText) {
    return `"${sourceCardName}" revealed "${revealedCardName}" (cost ${String(cost)}) — ${outcomeText}.`;
}
/**
 * Composes the single-sentence narrative for a `heroEffectResolved` event when an
 * "each other player's deck" reveal finds no other deck (D-24639 — Hypnotic Charm's
 * Instinct half in a 1-player game, or every other deck and discard exhausted).
 *
 * Pure + byte-stable, third person with no "Player N" prefix (the sibling
 * composers' voice). The source name is resolved by the fire site.
 *
 * // why: D-24639 — the printed "each other player's deck" has nothing to act on without
 * another deck, so nothing happens (like Covering Fire in a 1-player game), but the player
 * should SEE that the ability resolved rather than wonder whether it was skipped.
 *
 * @param sourceCardName - Human-facing name of the hero card whose ability resolved.
 * @returns A single English sentence for the notable-event overlay.
 */
export function composeNoOtherDeckNarrative(sourceCardName) {
    return `"${sourceCardName}" had no other player's deck to reveal.`;
}
// ---------------------------------------------------------------------------
// Excessive Violence fire narrative (WP-746)
// ---------------------------------------------------------------------------
/**
 * Composes the single-sentence narrative for an `excessiveViolenceFired` event
 * (WP-746 / D-24569).
 *
 * Pure + byte-stable: given the same inputs, returns identical output. Unlike the
 * sibling reveal composers, this one NAMES the player (the fire is a
 * whole-turn overspend beat framed around the acting player, not a single card),
 * so the fire site passes the resolved `playerLabel` (e.g. `"Player 0"`) and the
 * composer keeps its no-`G` purity. `firedCount` is the number of enrolled EV
 * cards that fired ≥1 inner effect (NOT the inner-effect count) — a single
 * non-nested ternary picks the singular/plural noun (the `composeFightNarrative`
 * clause style; no chained ternary per the code-style rules).
 *
 * @param playerLabel - Human-facing label of the fighting player (e.g. "Player 0").
 * @param firedCount - Count of enrolled EV cards that fired ≥1 inner effect (>= 1).
 * @returns A single English sentence for the notable-event overlay + frame log.
 */
export function composeExcessiveViolenceFiredNarrative(playerLabel, firedCount) {
    const abilityWord = firedCount === 1 ? 'ability' : 'abilities';
    return `${playerLabel} unleashes Excessive Violence, firing ${String(firedCount)} ${abilityWord}.`;
}
//# sourceMappingURL=notableEvents.compose.js.map